/**
 * Integrytas Group - Middleware Pipeline Hook Engine (PHE)
 * Page: PHE.HOOKS
 *
 * Register workspace-scoped hooks at the BEFORE / INSTEAD / AFTER boundaries of
 * the Common Routine, manage the CALL_FUNCTION whitelist, and review the run log.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'PHE.HOOKS';

interface HookRow {
  phe_hook_id: number; internal_code: string; name: string; workspace_code: string; entity_name: string;
  phase: 'BEFORE' | 'INSTEAD' | 'AFTER'; operation: 'ADD' | 'CHANGE' | 'DELETE' | 'ANY';
  conditions: unknown; action_type: 'TRANSFORM' | 'ABORT' | 'CALL_FUNCTION' | 'WEBHOOK' | 'NOTIFY';
  action_config: unknown; on_error: 'FAIL_OPEN' | 'FAIL_CLOSED'; priority: number; is_active: 'Y' | 'N';
}
interface FnRow { phe_function_whitelist_id: number; function_name: string; description: string | null; is_active: 'Y' | 'N' }
interface LogRow { phe_hook_log_id: number; internal_code: string | null; workspace_code: string | null; entity_name: string | null; phase: string | null; outcome: string | null; message: string | null; at: string }

const PHASES = ['BEFORE', 'INSTEAD', 'AFTER'] as const;
const OPS = ['ANY', 'ADD', 'CHANGE', 'DELETE'] as const;
const ACTIONS = ['TRANSFORM', 'ABORT', 'CALL_FUNCTION', 'WEBHOOK', 'NOTIFY'] as const;
// §17.B11: labelled constant for hook-run outcome codes (CHECK enum).
const OUTCOME_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  OK:       { labelKey: 'phe.outcome.OK',       label: 'OK',       variant: 'success' },
  SKIPPED:  { labelKey: 'phe.outcome.SKIPPED',  label: 'Skipped',  variant: 'neutral' },
  ABORTED:  { labelKey: 'phe.outcome.ABORTED',  label: 'Aborted',  variant: 'danger'  },
  OVERRODE: { labelKey: 'phe.outcome.OVERRODE', label: 'Overrode', variant: 'warning' },
  ERROR:    { labelKey: 'phe.outcome.ERROR',    label: 'Error',    variant: 'danger'  },
};

const LOG_GRID_COLS: GridColumnDef[] = [
  { key: 'at',             header: 'When',      defaultWidth: 170 },
  { key: 'internal_code',  header: 'Hook',      defaultWidth: 160 },
  { key: 'context',        header: 'Context',   defaultWidth: 220 },
  { key: 'outcome',        header: 'Outcome',   defaultWidth: 110 },
  { key: 'message',        header: 'Message',   defaultWidth: 260 },
];

export interface PheHooksPageProps { session: AuthSession }

export function PheHooksPage({ session }: PheHooksPageProps) {
  return (
    <PersonalizationProvider pageCode="PHE.HOOKS" session={session}>
      <PheHooksPageInner session={session} />
    </PersonalizationProvider>
  );
}

function PheHooksPageInner({ session }: PheHooksPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [hooks, setHooks] = useState<HookRow[]>([]);
  const [fns, setFns] = useState<FnRow[]>([]);
  const [log, setLog] = useState<LogRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets. phase/outcome scope the run-log list;
  // active (§17.B12) scopes the hooks list and defaults to Active-only on first load.
  const [facets, setFacets] = useState<{ phase: string[]; outcome: string[]; active: string[] }>({ phase: [], outcome: [], active: ['Y'] });

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [h, f, l] = await Promise.all([
        apiDb.from('phe_hook').select('phe_hook_id, internal_code, name, workspace_code, entity_name, phase, operation, conditions, action_type, action_config, on_error, priority, is_active').order('phase').order('priority'),
        apiDb.from('phe_function_whitelist').select('phe_function_whitelist_id, function_name, description, is_active').order('function_name'),
        apiDb.from('phe_hook_log').select('phe_hook_log_id, internal_code, workspace_code, entity_name, phase, outcome, message, at').order('at', { ascending: false }).limit(60),
      ]);
      if (h.error) throw h.error;
      setHooks((h.data ?? []) as HookRow[]); setFns((f.data ?? []) as FnRow[]); setLog((l.data ?? []) as LogRow[]);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  // Distinct facet options derived from the loaded log rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: (string | null)[]) => [...new Set(vals.filter((v): v is string => !!v))].sort();
    return {
      phase:   uniq(log.map(l => l.phase)).map(v => ({ value: v, label: v })),
      outcome: uniq(log.map(l => l.outcome)).map(v => ({ value: v, label: v })),
      active: [
        { value: 'Y', label: t('common.boolean.active', 'Active') },
        { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
      ],
    };
  }, [log, t]);

  const facetsActive = facets.phase.length + facets.outcome.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  // Clear-all resets to All (empty) — never back to the ['Y'] default.
  const clearAll = () => { setFilter(''); setFacets({ phase: [], outcome: [], active: [] }); };

  // Hooks list filtered by the Active facet; preserve the original index so
  // patch()/saveHook() still address the right row in the full hooks array.
  const filteredHooks = useMemo(
    () => hooks
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => facets.active.length === 0 || facets.active.includes(row.is_active)),
    [hooks, facets.active],
  );

  const filteredLog = useMemo(() => {
    // A row passes a facet when no values are selected, or it matches one (OR
    // within a facet, AND across facets).
    const matchesFacets = (l: LogRow) =>
      (facets.phase.length   === 0 || facets.phase.includes(l.phase ?? '')) &&
      (facets.outcome.length === 0 || facets.outcome.includes(l.outcome ?? ''));
    const q = filter.trim().toLowerCase();
    const base = !q ? log : log.filter(l =>
      (l.internal_code ?? '').toLowerCase().includes(q) ||
      (l.entity_name ?? '').toLowerCase().includes(q) ||
      (l.workspace_code ?? '').toLowerCase().includes(q) ||
      (l.phase ?? '').toLowerCase().includes(q) ||
      (l.outcome ?? '').toLowerCase().includes(q) ||
      (l.message ?? '').toLowerCase().includes(q));
    return base.filter(matchesFacets);
  }, [log, filter, facets]);

  // §17.B13: expand/contract related-children for the run-log grid. A log row's
  // internal_code references its hook definition (already loaded) — resolve its
  // name, never the raw code.
  const detail = useExpandedRows<number>();
  const buildLogGroups = (logId: number): RelatedChildGroup[] => {
    const l = log.find(x => x.phe_hook_log_id === logId);
    const h = l?.internal_code ? hooks.find(x => x.internal_code === l.internal_code) : undefined;
    return [
      {
        key: 'hook',
        table: 'phe_hook', pkColumn: 'phe_hook_id',
        title: t('phe.related.hook', 'Hook'),
        items: h ? [{ id: h.phe_hook_id, label: h.name || h.internal_code, title: h.internal_code }] : [],
      },
    ];
  };
  const hasLogRelated = (logId: number) => buildLogGroups(logId).some(g => g.items.length > 0);

  const addHook = async () => {
    await routedInsert(session, 'phe_hook', { internal_code: `HOOK_${Date.now()}`, name: 'New hook', workspace_code: '', entity_name: '*', phase: 'BEFORE', operation: 'ANY', conditions: {}, action_type: 'TRANSFORM', action_config: {}, on_error: 'FAIL_OPEN', priority: 100, is_active: 'N' });
    await reload();
  };
  const saveHook = async (r: HookRow, condText: string, cfgText: string) => {
    let conditions: unknown, action_config: unknown;
    try { conditions = JSON.parse(condText || '{}'); } catch { setError(t('phe.error.conditions_json', '{{code}}: conditions is not valid JSON.', { code: r.internal_code })); return; }
    try { action_config = JSON.parse(cfgText || '{}'); } catch { setError(t('phe.error.action_config_json', '{{code}}: action_config is not valid JSON.', { code: r.internal_code })); return; }
    const { error: e } = await routedUpdate(session, 'phe_hook', 'phe_hook_id', r.phe_hook_id, {
      internal_code: r.internal_code.trim(), name: r.name.trim(), workspace_code: r.workspace_code.trim(), entity_name: r.entity_name.trim() || '*',
      phase: r.phase, operation: r.operation, conditions, action_type: r.action_type, action_config, on_error: r.on_error, priority: r.priority, is_active: r.is_active,
    });
    if (e) setError(e.message); else setError(null);
  };
  const delHook = async (id: number) => { await routedDelete(session, 'phe_hook', 'phe_hook_id', id); setHooks(hs => hs.filter(x => x.phe_hook_id !== id)); };

  const addFn = async () => { await routedInsert(session, 'phe_function_whitelist', { function_name: 'public.my_hook_fn', description: '', is_active: 'N' }); await reload(); };
  const saveFn = async (r: FnRow) => { const { error: e } = await routedUpdate(session, 'phe_function_whitelist', 'phe_function_whitelist_id', r.phe_function_whitelist_id, { function_name: r.function_name.trim(), description: r.description?.trim() || null, is_active: r.is_active }); if (e) setError(e.message); };
  const delFn = async (id: number) => { await routedDelete(session, 'phe_function_whitelist', 'phe_function_whitelist_id', id); setFns(fs => fs.filter(x => x.phe_function_whitelist_id !== id)); };

  const patch = (i: number, p: Partial<HookRow>) => setHooks(hs => hs.map((x, j) => j === i ? { ...x, ...p } : x));

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('phe.title', 'Pipeline Hooks')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('phe.filter_placeholder', 'Filter run log…')}
          className="h-7 w-60 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter label={t('phe.col.phase', 'Phase')}     options={facetOptions.phase}   selected={facets.phase}   onChange={v => setFacets(f => ({ ...f, phase: v }))} />
        <FacetFilter label={t('phe.col.outcome', 'Outcome')} options={facetOptions.outcome} selected={facets.outcome} onChange={v => setFacets(f => ({ ...f, outcome: v }))} />
        <FacetFilter label={t('common.boolean.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
        <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('phe.refresh', 'Refresh')}</button>
        {canAdd && (<button onClick={() => void addHook()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('phe.add_hook', '+ Hook')}</button>)}
        {busy && <span className="text-csm-xs text-csm-watermark">{t('phe.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      <div className="flex-1 overflow-auto p-3">
        {error && (
          <ErrorBanner
            message={t('phe.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <Section title={t('phe.section.hooks', 'Hooks')}>
          {filteredHooks.map(({ row: r, index: i }) => (
            <div key={r.phe_hook_id} className="mb-2 rounded border border-csm-border-soft p-2">
              <div className="mb-1 flex flex-wrap items-center gap-1">
                <input value={r.internal_code} onChange={e => patch(i, { internal_code: e.target.value })} placeholder="CODE" className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                <input value={r.name} onChange={e => patch(i, { name: e.target.value })} placeholder={t('phe.placeholder.name', 'name')} className="h-7 w-44 rounded border border-csm-border px-1 text-csm-xs" />
                <select value={r.phase} onChange={e => patch(i, { phase: e.target.value as HookRow['phase'] })} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{PHASES.map(p => <option key={p} value={p}>{p}</option>)}</select>
                <input value={r.entity_name} onChange={e => patch(i, { entity_name: e.target.value })} placeholder={t('phe.placeholder.entity', 'entity (* = all)')} className="h-7 w-36 rounded border border-csm-border px-1 font-mono text-csm-2xs" />
                <select value={r.operation} onChange={e => patch(i, { operation: e.target.value as HookRow['operation'] })} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{OPS.map(o => <option key={o} value={o}>{o}</option>)}</select>
                <input value={r.workspace_code} onChange={e => patch(i, { workspace_code: e.target.value })} placeholder={t('phe.placeholder.workspace', 'workspace (blank = global)')} className="h-7 w-36 rounded border border-csm-border px-1 font-mono text-csm-2xs" />
                <select value={r.action_type} onChange={e => patch(i, { action_type: e.target.value as HookRow['action_type'] })} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}</select>
                <select value={r.on_error} onChange={e => patch(i, { on_error: e.target.value as HookRow['on_error'] })} className="h-7 rounded border border-csm-border px-1 text-csm-2xs" title={t('phe.tooltip.error_policy', 'error policy (BEFORE/INSTEAD)')}>{['FAIL_OPEN', 'FAIL_CLOSED'].map(o => <option key={o} value={o}>{o}</option>)}</select>
                <input type="number" value={r.priority} onChange={e => patch(i, { priority: Number(e.target.value) })} className="h-7 w-14 rounded border border-csm-border px-1 text-csm-2xs" title={t('phe.tooltip.priority', 'priority')} />
                <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={r.is_active === 'Y'} onChange={e => patch(i, { is_active: e.target.checked ? 'Y' : 'N' })} /> {t('phe.on', 'on')}</label>
                {canDelete && (<button onClick={() => void delHook(r.phe_hook_id)} className="ml-auto text-csm-xs text-csm-danger">{t('phe.del', 'del')}</button>)}
              </div>
              <div className="flex flex-wrap gap-2">
                <label className="flex-1 min-w-[16rem]">
                  <span className="text-csm-2xs uppercase tracking-wide text-csm-watermark">{t('phe.conditions_label', 'conditions (mta DSL)')}</span>
                  <textarea defaultValue={JSON.stringify(r.conditions ?? {}, null, 0)} id={`cond-${r.phe_hook_id}`} rows={2} className="w-full rounded border border-csm-border px-1 font-mono text-csm-2xs" placeholder='{"match":"all","conditions":[{"column":"status","op":"eq","value":"OPEN"}]}' />
                </label>
                <label className="flex-1 min-w-[16rem]">
                  <span className="text-csm-2xs uppercase tracking-wide text-csm-watermark">{t('phe.action_config_label', 'action_config')}</span>
                  <textarea defaultValue={JSON.stringify(r.action_config ?? {}, null, 0)} id={`cfg-${r.phe_hook_id}`} rows={2} className="w-full rounded border border-csm-border px-1 font-mono text-csm-2xs" placeholder='{"set":{"reviewed_by":"{{actor_user_id}}"}} | {"message":"blocked"} | {"function":"public.my_fn"} | {"category":"SYSTEM_ALERTS","title":"...","body":"...","recipients":{...}}' />
                </label>
                {canChange && (<button onClick={() => { const c = (document.getElementById(`cond-${r.phe_hook_id}`) as HTMLTextAreaElement)?.value ?? '{}'; const g = (document.getElementById(`cfg-${r.phe_hook_id}`) as HTMLTextAreaElement)?.value ?? '{}'; void saveHook(hooks[i], c, g); }} className="h-7 self-end rounded border border-primary bg-primary px-3 text-csm-xs text-white hover:opacity-90">{t('phe.save', 'Save')}</button>)}
              </div>
            </div>
          ))}
          {filteredHooks.length === 0 && (
            hooks.length === 0
              ? <EmptyState variant="no-data" title={t('phe.empty.hooks', 'No hooks. Click “+ Hook”.')} action={canAdd ? <button onClick={() => void addHook()} className="rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('phe.add_hook', '+ Hook')}</button> : undefined} />
              : <EmptyState variant="no-match" title={t('phe.empty.hooks_filtered', 'No hooks match the current filters.')} />
          )}
        </Section>

        <Section title={t('phe.section.whitelist', 'CALL_FUNCTION Whitelist')} onAdd={addFn}>
          {fns.map((r, i) => (
            <div key={r.phe_function_whitelist_id} className="mb-1 flex flex-wrap items-center gap-1">
              <input value={r.function_name} onChange={e => setFns(fs => fs.map((x, j) => j === i ? { ...x, function_name: e.target.value } : x))} onBlur={() => void saveFn(fns[i])} placeholder="public.my_fn" className="h-7 w-56 rounded border border-csm-border px-1 font-mono text-csm-xs" />
              <input value={r.description ?? ''} onChange={e => setFns(fs => fs.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} onBlur={() => void saveFn(fns[i])} placeholder={t('phe.placeholder.fn_description', 'takes jsonb, returns jsonb')} className="h-7 w-72 rounded border border-csm-border px-1 text-csm-2xs" />
              <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={r.is_active === 'Y'} onChange={e => { setFns(fs => fs.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveFn(fns[i])} /> {t('phe.on', 'on')}</label>
              {canDelete && (<button onClick={() => void delFn(r.phe_function_whitelist_id)} className="text-csm-xs text-csm-danger">{t('phe.del', 'del')}</button>)}
            </div>
          ))}
          {fns.length === 0 && (
            <EmptyState variant="no-data" title={t('phe.empty.whitelist', 'No whitelisted functions. CALL_FUNCTION hooks can only invoke functions listed here.')} />
          )}
        </Section>

        <Section title={t('phe.section.run_log', 'Run Log')}>
          <PersonalizedGrid componentKey="grid" columns={LOG_GRID_COLS}>
            {(visibleCols, widths) => (
              <table className="w-max min-w-full border-collapse text-csm-sm">
                <thead className="bg-csm-surface-alt text-csm-2xs uppercase tracking-wider text-csm-ink-soft">
                  <tr>
                    <th className="w-7 border-b border-csm-border px-2 py-1" title={t('common.related.header', 'Related records')} />
                    {visibleCols.map(col => (
                      <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('phe.col.' + col.key, col.header)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredLog.map(l => (
                    <Fragment key={l.phe_hook_log_id}>
                    <tr className="border-b border-csm-border-soft">
                      <td className="px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton open={detail.isOpen(l.phe_hook_log_id)} hasChildren={hasLogRelated(l.phe_hook_log_id)} onToggle={() => detail.toggle(l.phe_hook_log_id)} expandLabel={t('common.related.show', 'Show related records')} collapseLabel={t('common.related.hide', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="px-2 py-1 text-csm-2xs"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'at'            && <span className="text-csm-watermark">{new Date(l.at).toLocaleString()}</span>}
                          {col.key === 'internal_code' && <span className="font-mono text-csm-xs">{l.internal_code}</span>}
                          {col.key === 'context'       && <span className="text-csm-ink-soft">{l.phase} · {l.entity_name}{l.workspace_code ? ` · ${l.workspace_code}` : ''}</span>}
                          {col.key === 'outcome'       && (() => {
                            const meta = OUTCOME_META[l.outcome ?? ''];
                            return <StatusBadge label={meta ? t(meta.labelKey, meta.label) : (l.outcome ?? '')} variant={meta?.variant ?? 'neutral'} />;
                          })()}
                          {col.key === 'message'       && <span className="text-csm-danger">{l.message}</span>}
                        </td>
                      ))}
                    </tr>
                    {detail.isOpen(l.phe_hook_log_id) && (
                      <tr><td colSpan={visibleCols.length + 1} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildLogGroups(l.phe_hook_log_id)} session={session} /></td></tr>
                    )}
                    </Fragment>
                  ))}
                  {filteredLog.length === 0 && (
                    <tr><td colSpan={visibleCols.length + 1} className="px-2 py-3">
                      <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('phe.empty.run_log', 'No hook runs yet.')} />
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </PersonalizedGrid>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, onAdd, children }: { title: string; onAdd?: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <section className="mb-5">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-csm-sm font-semibold text-csm-ink">{title}</h3>
        {onAdd && <button onClick={onAdd} className="h-6 rounded border border-csm-border bg-white px-2 text-csm-xs hover:bg-csm-surface-alt">{t('phe.add', '+ Add')}</button>}
      </div>
      {children}
    </section>
  );
}
